import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "../../../utils/api/axios";
import toast from "react-hot-toast";
import { getUserRole, UserRole } from "../../../utils/api/endpoints/user/credentials";

// Define the shape of the Asset data
export interface AssetData {
    id?: string;
    company_id?: number | string;
    // Step 1: Identity
    name: string;
    summary: string;
    risk_owner: string;
    technical_owner: string;
    technical_owner_same: boolean;
    technical_owner_invite_later: boolean;

    // Step 2: Lifecycle
    lifecycle_status: string;
    lifecycle_status_other: string;
    change_frequency: string;
    change_frequency_other: string;
    deployment_model: string;
    deployment_model_other: string;
    primary_audience: string[];

    // Step 3: Classification
    category: string;
    category_other: string;
    primary_type: string[];
    mobile_platforms?: string[]; // New for mobile app support
    primary_type_other: string;
    includes: string[];
    includes_other: string;

    // Step 4: Criticality
    criticality: string;
    criticality_answers: Record<string, string>;
    compliance_frameworks: string[];
    compliance_other: string;

    // Step 4: Data & Privacy
    data_classification: string;
    data_classification_other: string;
    sensitive_data_description: string;
    data_residency: string[];

    // Step 5: Access & Identity
    authentication_required: string;
    authorization_model: string[];
    authorization_model_other: string;
    identity_provider: string;
    user_roles: string;

    // Step 6: Security & Testing
    internet_exposure: string;
    allowed_testing_types: string[];
    testing_constraints: string;

    // Step 7: Governance
    legal_ownership_confirmed: boolean;
    approved_by: string | null; // Optional
    approval_date: string | null; // Optional
}

interface AssetCreationContextType {
    currentStep: number;
    direction: "forward" | "backward";
    assetData: AssetData;
    setAssetData: React.Dispatch<React.SetStateAction<AssetData>>;
    updateFields: (fields: Partial<AssetData>) => void;
    nextStep: () => void;
    prevStep: () => void;
    goToStep: (step: number) => void;
    isStepValid: (step: number) => boolean;
    isSaving: boolean;
    isSubmitting: boolean;
    isLoading: boolean;
    saveDraft: () => Promise<void>;
    submitAsset: () => Promise<any>;
    isFormValid: () => boolean;
    getInvalidSteps: () => number[];
}

const AssetCreationContext = createContext<AssetCreationContextType | undefined>(undefined);

const INITIAL_DATA: AssetData = {
    id: "",
    company_id: undefined,
    name: "",
    summary: "",
    risk_owner: "",
    technical_owner: "",
    technical_owner_same: false,
    technical_owner_invite_later: false,
    lifecycle_status: "Active",
    lifecycle_status_other: "",
    change_frequency: "Rarely",
    change_frequency_other: "",
    deployment_model: "SaaS",
    deployment_model_other: "",
    primary_audience: [],
    category: "",
    category_other: "",
    primary_type: [],
    mobile_platforms: [],
    primary_type_other: "",
    includes: [],
    includes_other: "",
    criticality: "",
    criticality_answers: {},
    compliance_frameworks: [],
    compliance_other: "",
    data_classification: "",
    data_classification_other: "",
    sensitive_data_description: "",
    data_residency: [],
    authentication_required: "",
    authorization_model: [],
    authorization_model_other: "",
    identity_provider: "",
    user_roles: "",
    internet_exposure: "",
    allowed_testing_types: [],
    testing_constraints: "",
    legal_ownership_confirmed: false,
    approved_by: null,
    approval_date: null,
};

export const AssetCreationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [currentStep, setCurrentStep] = useState(1); // Start at Step 1 (Identity)
    const [direction, setDirection] = useState<"forward" | "backward">("forward");
    const [assetData, setAssetData] = useState<AssetData>(INITIAL_DATA);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { id: urlId, companyId: urlCompanyId } = useParams<{ id?: string; companyId?: string }>();
    const [searchParams] = useSearchParams();

    // Sync company_id from route param, search param, or sessionStorage (Admin only)
    useEffect(() => {
        const currentRole = getUserRole();
        const isAdmin = currentRole !== undefined && [
            UserRole.ADMIN,
            UserRole.SUB_ADMIN,
            UserRole.ADMIN_MANAGER
        ].includes(currentRole);

        if (isAdmin) {
            const queryCid = searchParams.get("company_id") || searchParams.get("companyId");
            const resolvedCid = urlCompanyId || queryCid || sessionStorage.getItem("ctb_admin_active_company_id");
            if (resolvedCid) {
                const cidNum = Number(resolvedCid);
                setAssetData((prev) => ({ ...prev, company_id: !isNaN(cidNum) ? cidNum : resolvedCid }));
            }
        }
    }, [urlCompanyId, searchParams]);

    const updateFields = (fields: Partial<AssetData>) => {
        setAssetData((prev) => ({ ...prev, ...fields }));
    };

    const nextStep = () => {
        setDirection("forward");
        setCurrentStep((prev) => prev + 1);
    };

    const prevStep = () => {
        setDirection("backward");
        setCurrentStep((prev) => Math.max(1, prev - 1));
    };

    const goToStep = (step: number) => {
        setDirection(step > currentStep ? "forward" : "backward");
        setCurrentStep(step);
    };

    // Load draft if ID exists in URL
    useEffect(() => {
        const loadDraft = async (id: string) => {
            setIsLoading(true);
            try {
                const response = await axios.get(`/assets/${id}`);
                if (response.data) {
                    setAssetData(response.data);
                    if (response.data.company_id) {
                        sessionStorage.setItem("ctb_admin_active_company_id", String(response.data.company_id));
                    }
                    setCurrentStep(1);
                }
            } catch (error) {
                console.error("Error loading asset draft:", error);
                toast.error("Failed to load asset draft.");
            } finally {
                setIsLoading(false);
            }
        };

        if (urlId && urlId !== assetData.id) {
            loadDraft(urlId);
        }
    }, [urlId, assetData.id]);

    const saveDraft = async () => {
        setIsSaving(true);
        try {
            const dataToSave = {
                ...assetData,
                company_id: assetData.company_id,
                status: "draft"
            };

            let response;
            if (assetData.id) {
                response = await axios.put(`/assets/${assetData.id}`, dataToSave);
            } else {
                response = await axios.post("/assets", dataToSave);
                updateFields({ id: response.data.id });
            }

            if (response.status === 200 || response.status === 201) {
                toast.success("Draft saved successfully!");
            }
        } catch (error: any) {
            console.error("Error saving draft:", error);
            toast.error(error.response?.data?.message || "Failed to save draft.");
        } finally {
            setIsSaving(false);
        }
    };

    const getInvalidSteps = (): number[] => {
        const invalid = [];
        for (let i = 1; i <= 7; i++) {
            if (!isStepValid(i)) invalid.push(i);
        }
        return invalid;
    };

    const isFormValid = (): boolean => {
        for (let i = 1; i <= 7; i++) {
            if (!isStepValid(i)) return false;
        }
        return true;
    };

    const submitAsset = async () => {
        if (!isFormValid()) {
            toast.error("Please complete all required fields before submitting.");
            return null;
        }
        setIsSubmitting(true);
        try {
            const payload = {
                ...assetData,
                company_id: assetData.company_id,
                status: "active",
            };

            const response = assetData.id
                ? await axios.put(`/assets/${assetData.id}`, payload)
                : await axios.post("/assets", payload);

            if (response.status === 201 || response.status === 200) {
                toast.success(
                    (t) => (
                        <div className="flex flex-col gap-1">
                            <span className="font-bold">Asset created successfully!</span>
                            <span className="text-xs opacity-80">Next step: Add a target to this asset.</span>
                        </div>
                    ),
                    { duration: 6000 }
                );
                return response.data;
            }
        } catch (error: any) {
            console.error("Error creating asset:", error);
            toast.error(error.response?.data?.message || "Failed to create asset.");
        } finally {
            setIsSubmitting(false);
        }
        return null;
    };

    const isStepValid = (step: number): boolean => {
        // Basic validation logic per step
        switch (step) {
            case 1: // Identity
                const currentRole = getUserRole();
                const isAdmin = currentRole !== undefined && [UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(currentRole);
                if (isAdmin && !assetData.company_id) return false;
                if (!assetData.name || !assetData.summary || !assetData.risk_owner) return false;
                if (!assetData.technical_owner && !assetData.technical_owner_invite_later && !assetData.technical_owner_same) return false;
                return true;
            case 2: // Lifecycle
                if (!assetData.lifecycle_status || !assetData.change_frequency || !assetData.deployment_model || assetData.primary_audience.length === 0) return false;
                if (assetData.lifecycle_status === "Other" && !assetData.lifecycle_status_other) return false;
                if (assetData.change_frequency === "Other" && !assetData.change_frequency_other) return false;
                if (assetData.deployment_model === "Other" && !assetData.deployment_model_other) return false;
                return true;
            case 3: // Classification (Combined 3 + 4)
                if (!assetData.category || !assetData.primary_type || assetData.primary_type.length === 0 || !assetData.criticality) return false;
                if (assetData.category === "Other" && !assetData.category_other) return false;
                if (assetData.primary_type.includes("Other") && !assetData.primary_type_other) return false;
                return true;
            case 4: // Data & Privacy (Old 5)
                if (!assetData.data_classification) return false;
                if (assetData.data_classification === "Other" && !assetData.data_classification_other) return false;
                return true;
            case 5: // Access (Old 6)
                if (!assetData.authentication_required) return false;
                if (assetData.authentication_required !== "None" && (assetData.authorization_model || []).includes("Other") && !assetData.authorization_model_other) return false;
                return true;
            case 6: // Security (Old 7)
                return !!(assetData.internet_exposure && (assetData.allowed_testing_types?.length > 0));
            case 7: // Governance (Old 8)
                return assetData.legal_ownership_confirmed;
            default:
                return true;
        }
    };

    return (
        <AssetCreationContext.Provider
            value={{
                currentStep,
                direction,
                assetData,
                setAssetData,
                updateFields,
                nextStep,
                prevStep,
                goToStep,
                isStepValid,
                isSaving,
                isSubmitting,
                isLoading,
                saveDraft,
                submitAsset,
                isFormValid,
                getInvalidSteps,
            }}
        >
            {children}
        </AssetCreationContext.Provider>
    );
};

export const useAssetCreation = () => {
    const context = useContext(AssetCreationContext);
    if (!context) {
        throw new Error("useAssetCreation must be used within an AssetCreationProvider");
    }
    return context;
};
